<script setup lang="ts">
import { TaskStatus, type OrganisationAgentTaskInDb } from '@/api';
import { computed, onMounted, ref, useTemplateRef, watch, type CSSProperties } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import Layout from '@/components/design/layout/Layout.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaChat from '@/components/design/domain/chat/GaiaChat.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaListItem from '@/components/design/composite/GaiaListItem.vue';
import GaiaStatusIndicator from '@/components/design/base/GaiaStatusIndicator.vue';
import GaiaSectionHeader from '@/components/design/base/GaiaSectionHeader.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import HomeQuickActions from '@/components/design/domain/home/HomeQuickActions.vue';
import HomeNoOrganisation from '@/components/design/domain/home/HomeNoOrganisation.vue';
import CreateOrganisationModal from '@/components/organisation/CreateOrganisationModal.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import { gaiaMotion } from '@/components/design/themes/tokens';
import {
  attentionStatus,
  attentionStatusLabel,
  attentionTarget,
  attentionTitle,
  attentionUpdatedAt,
  isSigningAttention,
  SIGNING_ACTION_VERB,
  type UserAttentionItem,
} from '@/components/design/domain/attention/attention';
import { IconSign } from '@/components/design/icons';
import { parseUidList } from '@/utils/chat/contextItems';
import { useAuthStore } from '@/stores/auth';
import { useUserAttentionStore } from '@/stores/userAttentionStore';
import { useOrganizationStore } from '@/stores/organizationStore';
import { useTourStore } from '@/stores/tourStore';
import { organisationsTour, setJoinedOrganisation } from '@/tours/organisations';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { OrganisationAgentTaskService } from '@/services/organisationAgentTaskService';
import { isOfficeAddinHost } from '@/office/utils';
import { logger } from '@/utils/logger';

dayjs.extend(relativeTime);

const route = useRoute();

const router = useRouter();
const authStore = useAuthStore();
const userAttentionStore = useUserAttentionStore();
const organizationStore = useOrganizationStore();
const toast = useGaiaToast();

/**
 * A user can be signed in without belonging to any organisation. Everything
 * below the greeting is org-scoped, so home then becomes the place that says
 * so and offers the way in: the organisations they can switch into, and (when
 * the deployment allows self-service) creating one.
 */
const hasOrganisation = computed(() => authStore.hasActiveOrganisation);
const showCreateOrganisation = ref(false);
const switchingUid = ref<string | null>(null);

// Same landing as the sidebar's switcher: a switch re-scopes every request, so
// a full document load onto home drops the cached state of the old (or no)
// organisation.
async function switchToOrganisation(uid: string): Promise<void> {
  if (switchingUid.value) return;
  switchingUid.value = uid;
  const switched = await organizationStore.switchCurrentUserOrganization(uid);
  if (!switched) {
    switchingUid.value = null;
    toast.error(organizationStore.error ?? 'Failed to switch organisation');
    return;
  }
  window.location.assign('/');
}

/**
 * Planned-downtime notice, set per environment through VITE_MAINTENANCE_MESSAGE.
 * The sign-in pages read the same variable; home is where it reaches people who
 * are already inside the app (it used to sit in a topbar that no longer exists).
 * An empty value means there is nothing to announce.
 */
// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty/falsy value must use the fallback
const maintenanceMessage = computed(() => import.meta.env.VITE_MAINTENANCE_MESSAGE || '');

// Greeting
const firstName = computed(() => authStore.backendUser?.full_name.split(' ')[0] ?? '');
const greeting = computed(() => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
});

/**
 * The quiet line under the greeting. A greeting alone is one floating sentence;
 * the date gives the block a second line to sit on and tells you the app is
 * showing you *today*.
 */
const todayLabel = computed(() => dayjs().format('dddd, D MMMM'));

/** Initials on the user's own chat bubbles — "Janina Möllmann" → "JM". */
const userInitials = computed(() =>
  (authStore.backendUser?.full_name ?? '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('')
);

const askValue = ref('');

/**
 * The whole page reacts to this: the greeting and the tail below the composer
 * (quick actions, action required, agent activity) collapse, so the chat gets
 * the column to itself. `GaiaChat` owns the transcript; this view owns
 * everything around it.
 */
const chatting = ref(false);

// ── Chat ⇄ URL ──────────────────────────────────────────────────────────────
//
// The open conversation lives in the query, on the same `?thread_uid=`
// contract as /gaia-chat. Two things depend on it: the sidebar's Home item is
// a plain `router.push('/')`, which the router discards as a duplicate unless
// the chat put something in the query to navigate away from; and the back
// button should walk conversations. So conversation switches `push` (unlike
// the old view, which only ever `replace`d and left back unusable) while a new
// thread merely *learning* its uid `replace`s — that is one step gaining an
// identity, not a new one.

const chatRef = useTemplateRef<{
  openThread: (uid: string) => Promise<void>;
  newChat: () => void;
  openHistory: () => void;
  seedContext: (selection: {
    fileUids?: string[];
    contractUids?: string[];
    contractFileUids?: string[];
  }) => void;
}>('chat');

/** The conversation GaiaChat currently has on screen. */
const openThreadUid = ref<string | null>(null);

function queryString(value: unknown): string | null {
  if (typeof value === 'string' && value.trim().length > 0) return value;
  if (Array.isArray(value)) {
    const first = value.find(item => typeof item === 'string' && item.trim().length > 0);
    return typeof first === 'string' ? first : null;
  }
  return null;
}

const routeThreadUid = computed(() => queryString(route.query.thread_uid));
/** `?chat=1` is chat mode before the thread exists; a thread uid implies it. */
const routeChatOpen = computed(
  () => routeThreadUid.value !== null || queryString(route.query.chat) !== null
);

/**
 * Documents handed over by "Ask AI" elsewhere in the app (`askAiRoute`). The
 * `new_chat` stamp is what makes two asks about different rows distinct
 * locations, so it is also what tells us a hand-off is a *new* one to seed.
 */
const routeAskStamp = computed(() => queryString(route.query.new_chat));
let seededAskStamp: string | null = null;

/** URL → view. Also what back and forward run through. */
function applyRouteToChat(): void {
  chatting.value = routeChatOpen.value;
  if (!routeChatOpen.value) {
    openThreadUid.value = null;
    seededAskStamp = null;
    return;
  }
  const uid = routeThreadUid.value;
  // Claim the uid before the load: `openThread` awaits a rejoined run to the
  // end, so nothing downstream may depend on that promise having settled.
  if (uid && uid !== openThreadUid.value) {
    openThreadUid.value = uid;
    void chatRef.value?.openThread(uid);
  } else if (!uid && openThreadUid.value) {
    openThreadUid.value = null;
    chatRef.value?.newChat();
    // That wiped the composer, so whatever was seeded here is gone with it.
    seededAskStamp = null;
  }

  // Seeding comes last: starting a new chat clears the composer, and a
  // hand-off arriving on the same tick must survive it.
  const askStamp = routeAskStamp.value;
  if (askStamp && askStamp !== seededAskStamp) {
    seededAskStamp = askStamp;
    chatRef.value?.seedContext({
      fileUids: parseUidList(route.query.vault_files),
      contractUids: parseUidList(route.query.contracts),
      contractFileUids: parseUidList(route.query.contract_files),
    });
  }
}

watch([routeChatOpen, routeThreadUid, routeAskStamp], applyRouteToChat);

// View → URL. Both handlers are guarded by comparing against the URL rather
// than by a "currently applying" flag: the component echoes every change back,
// including the ones we just applied from a back/forward, and an echo that
// already matches the query is simply nothing to write.
function onChattingChange(next: boolean): void {
  chatting.value = next;
  if (next === routeChatOpen.value) return;
  void router.push({ query: next ? { chat: '1' } : {} });
}

function onThreadChange(uid: string | null): void {
  openThreadUid.value = uid;
  const isNewThread = routeChatOpen.value && routeThreadUid.value === null;
  if (uid === routeThreadUid.value || (uid === null && isNewThread)) return;
  // A thread that just learned its uid is this step gaining an identity, so it
  // replaces; anything else is a move to another conversation, so it pushes.
  void (uid !== null && isNewThread
    ? router.replace({ query: { thread_uid: uid } })
    : router.push({ query: uid ? { thread_uid: uid } : { chat: '1' } }));
}

/**
 * Gap between the board's rows. Named because the banner has to restate it: it
 * sits outside the column, which closes up in chat mode.
 */
const COLUMN_GAP = 26;

/** Collapse boxes, per mock 5a. Both stay mounted — a v-if can't animate. */
const collapseStyle = (open: boolean, height: string, shift: string): CSSProperties => ({
  maxHeight: open ? height : '0px',
  opacity: open ? '1' : '0',
  transform: open ? 'translateY(0)' : shift,
  overflow: 'hidden',
  transition: `max-height ${gaiaMotion.morph}, transform ${gaiaMotion.morph}, opacity ${gaiaMotion.fade}`,
});

const SUGGESTED_QUESTIONS = [
  'Run the NDA playbook on this file.',
  'Create a playbook to sense-check a DPA.',
  'Which contracts are off-playbook?',
  'How can i get started with contracts on Insights?',
];

// Action required = everything waiting on the user in the active organisation:
// chat runs that settled while they were away and signing turns.
const attentionItems = computed(() => userAttentionStore.items);

/**
 * How many rows each column shows; the rest are summarised by the "+x more"
 * line. Shared by both columns: they sit side by side now, so a different
 * count per side would just read as one of them being short.
 */
const LIST_LIMIT = 5;

const visibleAttentionItems = computed(() => attentionItems.value.slice(0, LIST_LIMIT));

/** "3 of 12" — shown over total, so a short list reads "1 of 1". */
const attentionCountLabel = computed(
  () => `${visibleAttentionItems.value.length} of ${userAttentionStore.total}`
);

const hiddenAttentionCount = computed(
  () => userAttentionStore.total - visibleAttentionItems.value.length
);

/** How long the longest-waiting item has been waiting, e.g. "4 days". */
const oldestAttentionWait = computed(() => {
  const timestamps = attentionItems.value.map(attentionUpdatedAt);
  if (!timestamps.length) return null;
  const oldest = timestamps.reduce((a, b) => (dayjs(a).isBefore(dayjs(b)) ? a : b));
  // `true` drops the "ago" suffix — the label supplies its own wording.
  return dayjs(oldest).fromNow(true);
});

/** Informational only — the design has no action on this line. */
const attentionMoreLabel = computed(() => {
  const wait = oldestAttentionWait.value;
  const more = `+${hiddenAttentionCount.value} more`;
  return wait ? `${more} · oldest waiting ${wait}` : more;
});

// Every row here is in the active organisation, so nothing needs a switch. A
// general-chat row opens its thread in the chat above, in place.
function openAttentionItem(item: UserAttentionItem): void {
  const target = attentionTarget(item);
  if (target) void router.push(target);
}

function goToActionRequired(): void {
  void router.push({ name: 'action-required' });
}

// Agent activity = the org's most recent agent tasks, the right-hand column
// under the composer. Unlike attention (whose store holds a full page that the
// chat badges share, sliced above), it asks for exactly the rows we show and
// reads the total off `result_count`.
const activeTasks = ref<OrganisationAgentTaskInDb[]>([]);

/** First tasks fetch has settled — tells "no activity" apart from "still loading". */
const tasksLoaded = ref(false);

// The tasks endpoint and the /intake-agents setup page are both org-admin-only,
// so the activity column only exists for org admins (same gate as the sidebar's
// Intake Agents entry).
const isOrgAdmin = computed(() => authStore.userPermissions.is_org_admin);

// 60/40 as spans of a ten-column grid; an empty column keeps its place and
// shows an empty state instead of handing its width to the other. A non-admin
// never gets the activity column, so attention takes the whole row. Below
// naive's `m` the two stack, because 40% of a narrow window is no place for
// these cards.
const attentionSpan = computed(() => (isOrgAdmin.value ? '1 m:6' : '1 m:10'));
const ACTIVITY_SPAN = '1 m:4';

/**
 * The grid already makes the two cells equal height; these pass that height
 * down so an *empty* card fills its column instead of ending short of the one
 * beside it. Rows have their own height, so a populated column ignores this.
 */
const columnStyle: CSSProperties = { height: '100%' };
const emptyStyle: CSSProperties = { flex: 1 };

const TASK_STATUS_INDICATOR: Record<
  TaskStatus,
  'running' | 'done' | 'risk' | 'neutral' | 'warning'
> = {
  [TaskStatus.RUNNING]: 'running',
  [TaskStatus.DONE]: 'done',
  [TaskStatus.FAILED]: 'risk',
  [TaskStatus.PENDING]: 'neutral',
  [TaskStatus.PARTIAL]: 'warning',
  [TaskStatus.WARNING]: 'warning',
  [TaskStatus.CANCELLED]: 'neutral',
  [TaskStatus.SKIPPED]: 'neutral',
};

const TASK_STATUS_LABEL: Record<TaskStatus, string> = {
  [TaskStatus.RUNNING]: 'Running',
  [TaskStatus.DONE]: 'Done',
  [TaskStatus.FAILED]: 'Failed',
  [TaskStatus.PENDING]: 'Pending',
  [TaskStatus.PARTIAL]: 'Partial',
  [TaskStatus.WARNING]: 'Warning',
  [TaskStatus.CANCELLED]: 'Cancelled',
  [TaskStatus.SKIPPED]: 'Skipped',
};

function humanizeTaskType(type: string): string {
  return type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

async function fetchRecentTasks(): Promise<void> {
  try {
    const result = await OrganisationAgentTaskService.search({
      text: '',
      limit: LIST_LIMIT,
      sort_key: 'created_at',
      sort_direction: 'desc',
    });
    activeTasks.value = result?.results ?? [];
  } catch (error) {
    logger.error('Failed to fetch active agent tasks:', error);
  } finally {
    tasksLoaded.value = true;
  }
}

function goToAgentTasks(): void {
  void router.push({ name: 'settings-agent-tasks' });
}

/**
 * `?joined=<org name>` is set by InviteAcceptView after an accepted invitation:
 * point straight at the workspace switcher (no "want a tour?" offer — it is
 * the answer to "where did my new organisation go"), then drop the param so a
 * reload does not replay it.
 */
function showJoinedOrganisation(): void {
  if (!('joined' in route.query)) return;
  setJoinedOrganisation(queryString(route.query.joined));
  useTourStore().startTour(organisationsTour.key);
  const query = { ...route.query };
  delete query.joined;
  void router.replace({ query });
}

onMounted(() => {
  if (isOfficeAddinHost()) {
    void router.replace('/vault');
    return;
  }
  // The attention store is already initialized app-wide by
  // useAuthenticatedBootstrap (see DefaultLayout), so this view only reads it.
  // The tasks search is org-admin-only server-side, so don't bother otherwise.
  if (isOrgAdmin.value) void fetchRecentTasks();
  showJoinedOrganisation();
  // Without an organisation there is no chat to drive.
  if (!hasOrganisation.value) return;
  // A reload or a pasted link lands here with the conversation already in the
  // query. The watcher above only fires on changes, so the first read is here —
  // after mount, when the GaiaChat ref exists to be driven.
  applyRouteToChat();
});
</script>

<template>
  <Layout>
    <!-- Above the greeting and outside its collapse, so it stays put once a
         conversation starts — downtime is worth reading either way. Outside the
         column too, carrying its own gap: the column below closes up in chat
         mode and would otherwise leave the banner flush against the chat. -->
    <GaiaBanner
      v-if="maintenanceMessage"
      type="warning"
      closable
      :style="{ marginBottom: `${COLUMN_GAP}px` }"
    >
      {{ maintenanceMessage }}
    </GaiaBanner>

    <!-- In chat mode the greeting and the tail are collapsed to nothing but are
         still flex children, and a gap the column owns outlives them — 26px
         above the chat and 26px below, which is what put home's chat off the y
         that /chat, mounting the same component bare, starts on. -->
    <n-flex vertical :size="chatting ? 0 : COLUMN_GAP">
      <!-- `display` is a light, wide-tracked serif; the old inline
           `font-weight: 600` fought exactly that and made the greeting read as
           a bold page title rather than a hello. Weight comes from the variant
           now, size from the scale. -->
      <div :style="collapseStyle(!chatting, '150px', 'translateY(-14px)')">
        <n-flex vertical align="center" :size="7" :style="{ padding: '16px 0 2px' }">
          <GaiaHeader
            :level="1"
            variant="display"
            size="hero"
            weight="medium"
            :style="{ margin: 0 }"
          >
            {{ greeting }}, {{ firstName }}.
          </GaiaHeader>
          <GaiaText size="body" tone="subtle">{{ todayLabel }}</GaiaText>
        </n-flex>
      </div>

      <n-flex v-if="!hasOrganisation" justify="center">
        <HomeNoOrganisation
          :organisations="authStore.organisations"
          :can-create="authStore.userPermissions.can_create_organisation ?? false"
          :switching-uid="switchingUid"
          @select="switchToOrganisation"
          @create="showCreateOrganisation = true"
        />
      </n-flex>

      <n-flex v-if="hasOrganisation" justify="center">
        <GaiaChat
          ref="chat"
          v-model:value="askValue"
          attachable
          placeholder="Ask anything about your contracts…"
          drop-hint="Drop files to attach them to your question"
          :chatting="chatting"
          :suggestions="SUGGESTED_QUESTIONS"
          :user-initials="userInitials"
          :style="chatting ? undefined : { 'max-width': '750px' }"
          @update:chatting="onChattingChange"
          @thread-change="onThreadChange"
          @open-reference="path => router.push(path)"
        />
      </n-flex>

      <div v-if="hasOrganisation" :style="collapseStyle(!chatting, '1500px', 'translateY(10px)')">
        <n-flex vertical :size="28">
          <n-flex vertical :size="12">
            <GaiaSectionHeader>Quick actions</GaiaSectionHeader>
            <HomeQuickActions />
          </n-flex>

          <!-- The two lists, 60/40. Both are "things you should look at", so
               they get the same row count and the same card row — only the
               copy inside differs. An empty column holds its place with an
               empty state; while its first fetch is still out it shows just
               the header, so the empty copy never flashes before data. -->
          <n-grid cols="1 m:10" responsive="screen" item-responsive :x-gap="24" :y-gap="28">
            <n-gi :span="attentionSpan">
              <n-flex vertical :size="12" :style="columnStyle">
                <n-flex align="center" justify="space-between">
                  <GaiaSectionHeader
                    :count="attentionItems.length ? attentionCountLabel : undefined"
                    >Action required</GaiaSectionHeader
                  >
                  <GaiaButton
                    v-if="attentionItems.length"
                    text
                    size="small"
                    @click="goToActionRequired"
                    >View all</GaiaButton
                  >
                </n-flex>

                <GaiaEmptyState
                  v-if="userAttentionStore.loaded && !attentionItems.length"
                  dashed
                  :style="emptyStyle"
                  title="Nothing needs your attention"
                >
                  When an agent run needs your input, fails, or finishes while you're away, or a
                  document is waiting for your signature, it shows up here.
                </GaiaEmptyState>

                <!-- Action cards: bold reason, then the item, with the wait
                     time trailing right (design's variant="card" row). A
                     signing row also carries "Sign/Approve now", which opens the
                     provider's page straight away. -->
                <n-flex v-if="attentionItems.length" vertical :size="8">
                  <GaiaListItem
                    v-for="item in visibleAttentionItems"
                    :key="item.uid"
                    variant="card"
                    :clickable="attentionTarget(item) !== null"
                    size="item"
                    @click="openAttentionItem(item)"
                  >
                    <template #leading>
                      <n-icon v-if="isSigningAttention(item)" :component="IconSign" :size="14" />
                      <GaiaStatusIndicator v-else :status="attentionStatus(item)" />
                    </template>
                    <GaiaText weight="semibold">{{ attentionStatusLabel(item) }}</GaiaText> —
                    <GaiaText>{{ attentionTitle(item) }}</GaiaText>
                    <template #trailing>
                      <n-flex align="center" :size="12" :wrap="false">
                        {{ dayjs(attentionUpdatedAt(item)).fromNow() }}
                        <GaiaButton
                          v-if="isSigningAttention(item)"
                          type="primary"
                          size="small"
                          @click.stop="userAttentionStore.openSigningLink(item)"
                          >{{ SIGNING_ACTION_VERB[item.reason] }} now ↗</GaiaButton
                        >
                      </n-flex>
                    </template>
                  </GaiaListItem>
                </n-flex>

                <GaiaText v-if="hiddenAttentionCount" tag="div" tone="subtle" size="body">
                  {{ attentionMoreLabel }}
                </GaiaText>
              </n-flex>
            </n-gi>

            <n-gi v-if="isOrgAdmin" :span="ACTIVITY_SPAN">
              <n-flex vertical :size="12" :style="columnStyle">
                <n-flex align="center" justify="space-between">
                  <GaiaSectionHeader>Agent activity</GaiaSectionHeader>
                  <GaiaButton v-if="activeTasks.length" text size="small" @click="goToAgentTasks"
                    >View all</GaiaButton
                  >
                </n-flex>

                <GaiaEmptyState
                  v-if="tasksLoaded && !activeTasks.length"
                  dashed
                  :style="emptyStyle"
                  title="No agent activity yet"
                >
                  Start by setting up an intake agent — it watches a mailbox and handles incoming
                  contracts for you.
                  <template #actions>
                    <GaiaButton type="primary" @click="router.push('/intake-agents')"
                      >Set up an intake agent</GaiaButton
                    >
                  </template>
                </GaiaEmptyState>

                <!-- Same row as an action card, one step down in urgency: the
                     task type is the title, its status only the leading dot
                     (hover it for the label). -->
                <n-flex v-if="activeTasks.length" vertical :size="8">
                  <GaiaListItem
                    v-for="task in activeTasks"
                    :key="task.uid"
                    variant="card"
                    :clickable="false"
                    size="item"
                  >
                    <template #leading>
                      <n-tooltip placement="top">
                        <template #trigger>
                          <GaiaStatusIndicator
                            :status="TASK_STATUS_INDICATOR[task.status]"
                            :aria-label="TASK_STATUS_LABEL[task.status]"
                          />
                        </template>
                        {{ TASK_STATUS_LABEL[task.status] }}
                      </n-tooltip>
                    </template>
                    <GaiaText weight="semibold">{{ humanizeTaskType(task.type) }}</GaiaText>
                    <template #trailing>
                      {{ dayjs(task.updated_at ?? task.created_at).fromNow() }}
                    </template>
                  </GaiaListItem>
                </n-flex>
              </n-flex>
            </n-gi>
          </n-grid>
        </n-flex>
      </div>
    </n-flex>

    <CreateOrganisationModal v-model:show="showCreateOrganisation" />
  </Layout>
</template>
